import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { sendFailureNotification } from "shared/toast_notifications"
import { allProjectsStore } from "../../../stores/all_projects_store"

// Connects to data-controller="suppliers--projects--projects-supplier"
//
// AutoLoad supplier for the org's project list. On page load it paginates
// GET /api/projects/list (looping until pagination_token is null), reads the
// wrapped success envelope (render_success nests the payload under `data`, so
// the projects live at json.data.projects and the next page token at
// json.data.pagination_token), accumulates every project into allProjectsStore,
// and clears the store on error.
//
// Member-count decision: the list endpoint returns only project fields (no
// member_count) and there is no batched count available from the API. Rather
// than issue N per-project /members fetches during the store load (which would
// slow initial render and risk partial failures blocking the whole list), the
// member count is left to lazy per-row fetching in the consuming management
// controller (Task 5.3). This keeps the supplier a single-responsibility list
// loader consistent with the other autoLoad suppliers, and never blocks the
// store load on member-count failures.
//
// Optional org-scope: the Projects management page needs the FULL org-wide project
// set (not just the caller's member-scoped projects). It sets the projectScopeToken
// value to a short-lived, signed organization-scope token minted server-side behind
// an authorization check (mirrors the locales supplier). When present, it is forwarded
// as the X-Project-Scope-Token header on every list request so the server widens
// results to the organization scope. Left unset, no header is sent and the requests
// stay member-scoped exactly as before.
export default class extends Controller {

    static stores = [
        allProjectsStore
    ]

    static values = {
        autoLoad: Boolean,
        projectScopeToken: String
    }

    connect() {
        useStore(this)

        // If autoLoad is enabled, load the org's projects immediately
        if (this.autoLoadValue) {
            this.loadProjects()
        }
    }

    /**
     * Load all projects (with pagination) and populate allProjectsStore
     */
    async loadProjects() {
        try {
            const projects = await this.fetchAllProjects()

            // Update the store with the fetched projects
            this.setAllProjectsStoreValue(projects)
        } catch (error) {
            console.error('Error loading projects:', {
                error: error.message,
                timestamp: new Date().toISOString()
            })

            // Show user-friendly error message
            sendFailureNotification('Unable to load projects. Please refresh the page.')

            // Clear projects on error
            this.setAllProjectsStoreValue([])
        }
    }

    /**
     * Fetch all projects with pagination handling.
     *
     * Reads the wrapped success envelope: render_success nests the payload under
     * `data`, so the projects for each page are at json.data.projects and the
     * next page token at json.data.pagination_token.
     */
    async fetchAllProjects() {
        const results = []
        let paginationToken = null

        do {
            const params = new URLSearchParams()
            if (paginationToken) {
                params.append('pagination_token', paginationToken)
            }
            const query = params.toString()

            const response = await fetch(`/api/projects/list${ query ? `?${ query }` : '' }`, {
                method: 'GET',
                headers: this._requestHeaders(),
                credentials: 'same-origin',
                signal: AbortSignal.timeout(30000)  // 30 second timeout
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`)
            }

            const json = await response.json()

            // Handle malformed response
            if (!json || typeof json !== 'object' || !json.data) {
                throw new Error('Malformed API response')
            }

            // Accumulate projects from this page (wrapped envelope: data.data.projects)
            if (Array.isArray(json.data.projects)) {
                results.push(...json.data.projects)
            }

            // Update pagination token (wrapped envelope: data.data.pagination_token)
            paginationToken = json.data.pagination_token
        } while (paginationToken != null)

        return results
    }

    /**
     * Build the request headers for project-list fetches. When an organization-scope token is
     * present (set by the Projects management page, which needs the full org-wide project set),
     * forward it as X-Project-Scope-Token so the server widens results to the organization
     * scope. Absent the token, no scope header is sent and requests stay member-scoped, exactly
     * as before.
     */
    _requestHeaders() {
        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }

        const token = this.hasProjectScopeTokenValue ? this.projectScopeTokenValue : ''
        if (token) {
            headers['X-Project-Scope-Token'] = token
        }

        return headers
    }

    /**
     * Manually refresh the projects list
     * (Can be called by other controllers after a create/delete)
     */
    refreshProjects() {
        this.loadProjects()
    }
}
